import { useEffect } from 'react';
import { useForm, type UseFormRegister, type FieldErrors } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  createQuestionSchema,
  type CreateQuestionFormData,
} from '../schemas/referee-question.schemas';
import type {
  CreateRefereeQuestionRequest,
  RefereeQuestion,
} from '../types/referee-question.types';

export interface UseQuestionFormReturn {
  register: UseFormRegister<CreateQuestionFormData>;
  errors: FieldErrors<CreateQuestionFormData>;
  isEditMode: boolean;
  submitHandler: (e?: React.BaseSyntheticEvent) => Promise<void>;
  reset: () => void;
}

export function useQuestionForm(
  editingQuestion: RefereeQuestion | null | undefined,
  onSubmit: (data: CreateRefereeQuestionRequest) => void,
  onUpdate?: (data: { questionId: string; text: string; isRequired: boolean }) => void
): UseQuestionFormReturn {
  const isEditMode = !!editingQuestion;
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<CreateQuestionFormData>({
    resolver: zodResolver(createQuestionSchema),
    defaultValues: { text: '', isRequired: true },
  });

  useEffect(() => {
    if (editingQuestion)
      reset({ text: editingQuestion.text, isRequired: editingQuestion.isRequired });
    else reset({ text: '', isRequired: true });
  }, [editingQuestion, reset]);

  const handleFormSubmit = (data: CreateQuestionFormData): void => {
    if (isEditMode && onUpdate && editingQuestion) {
      onUpdate({ questionId: editingQuestion.id, text: data.text, isRequired: data.isRequired });
    } else {
      onSubmit(data);
    }
    reset();
  };

  return { register, errors, isEditMode, submitHandler: handleSubmit(handleFormSubmit), reset };
}
